import React, { useEffect } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import styled from 'styled-components';
import { toRgba } from '../../../../../ui/theme/colors';
import { ItemTypes } from '../../../../editCourse/utils/dragTypes';

const Wrapper = styled.div`
  opacity: ${({ $isDragging }) => ($isDragging ? 0.5 : 1)};
`;

const DropZone = styled.div`
  height: ${({ $isOver }) => ($isOver ? '40px' : '4px')};
  background: ${({ $isOver, $themeColor }) => ($isOver ? toRgba($themeColor, 0.1) : 'transparent')};
  border: ${({ $isOver, $themeColor }) => ($isOver ? `2px dashed ${toRgba($themeColor, 0.4)}` : 'none')};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
`;

/**
 * DraggablePage - Wrapper component for draggable pages in the structure editor
 * @param {boolean} isQuiz - When true, disables dragging and prevents drops into this section
 */
const DraggablePage = ({ page, sectionId, index, onDrop, children, themeColor, isQuiz = false }) => {
  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: ItemTypes.PAGE,
      item: { pageId: page.id, pageName: page.name, sectionId, index },
      canDrag: !isQuiz,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [page.id, page.name, sectionId, index, isQuiz],
  );

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ItemTypes.PAGE,
      canDrop: () => !isQuiz,
      drop: (item) => {
        if (item.pageId !== page.id) {
          onDrop(item.pageId, sectionId, index);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [page.id, sectionId, index, onDrop, isQuiz],
  );

  const isRenderProp = typeof children === 'function';

  useEffect(() => {
    if (isRenderProp) {
      preview(getEmptyImage(), { captureDraggingState: true });
    }
  }, [isRenderProp, preview]);

  if (isRenderProp) {
    return (
      <>
        <DropZone ref={drop} $isOver={isOver && canDrop} $themeColor={themeColor} />
        <Wrapper $isDragging={isDragging}>{children({ dragRef: drag, isDragging })}</Wrapper>
      </>
    );
  }

  return (
    <>
      <DropZone ref={drop} $isOver={isOver && canDrop} $themeColor={themeColor} />
      <Wrapper ref={drag} $isDragging={isDragging}>
        {children}
      </Wrapper>
    </>
  );
};

export default DraggablePage;
